import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { COMPANY } from "@/i18n";

export const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "/#about", label: t.nav.about },
    { href: "/#categories", label: t.nav.categories },
    { href: "/#supply", label: t.nav.supply },
    { href: "/#offers", label: t.nav.offers },
    { href: "/#contact", label: t.nav.contact },
  ];

  const hashLink = (href) => (onHome ? href.replace("/", "") : href);

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/70 border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img
            src={COMPANY.logo}
            alt="Kruška Distribucija"
            className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover ring-1 ring-white/10 group-hover:ring-[#D4AF37]/60 transition-all"
          />
          <span className="font-display font-extrabold tracking-tight text-sm sm:text-base leading-none">
            KRUŠKA DISTRIBUCIJA
            <span className="block text-[9px] sm:text-[10px] font-body font-medium tracking-[0.28em] text-[#D4AF37]">
              KRUŠEVAC
            </span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={hashLink(l.href)}
              data-testid={`nav-link-${l.href.split("#")[1]}`}
              className="relative text-sm font-medium text-zinc-300 hover:text-white transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#D4AF37] hover:after:w-full after:transition-all after:duration-300"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/catalog"
            data-testid="nav-link-catalog"
            className="text-sm font-medium text-zinc-300 hover:text-white transition-colors"
          >
            {t.nav.catalog}
          </Link>
        </div>

        <div className="flex items-center gap-3 sm:gap-4">
          <div
            data-testid="language-switcher"
            className="flex items-center text-xs font-mono border border-white/15 rounded-full overflow-hidden"
          >
            {["sr", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-${l}`}
                onClick={() => setLang(l)}
                className={`px-3 py-1.5 uppercase transition-colors ${
                  lang === l ? "bg-[#D4AF37] text-black font-bold" : "text-zinc-400 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          <a
            href={hashLink("/#inquiry")}
            data-testid="nav-cta-inquiry"
            className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-full bg-[#D4AF37] text-black text-sm font-bold hover:bg-[#F3CE5A] active:scale-95 transition-all"
          >
            {t.nav.inquiry}
          </a>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-white p-1"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden overflow-hidden bg-black/95 border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={hashLink(l.href)}
                  data-testid={`mobile-nav-link-${l.href.split("#")[1]}`}
                  onClick={() => setOpen(false)}
                  className="text-lg font-display text-zinc-200"
                >
                  {l.label}
                </a>
              ))}
              <Link to="/catalog" data-testid="mobile-nav-link-catalog" onClick={() => setOpen(false)} className="text-lg font-display text-zinc-200">
                {t.nav.catalog}
              </Link>
              <a
                href={hashLink("/#inquiry")}
                data-testid="mobile-nav-cta-inquiry"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex justify-center px-5 py-3 rounded-full bg-[#D4AF37] text-black font-bold"
              >
                {t.nav.inquiry}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
